"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSttStream = void 0;
const react_1 = require("react");
const SAMPLE_RATE = 8000;
const env = globalThis._env_ || {};
const STT_WS_URL = env.REACT_APP_STT_WS_URL ||
    `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.hostname}/ws/stt`; //debug port was 8001
const downsampleBuffer = (buffer, inSampleRate, outSampleRate) => {
    if (outSampleRate === inSampleRate)
        return buffer;
    const ratio = inSampleRate / outSampleRate;
    const newLen = Math.floor(buffer.length / ratio);
    const result = new Float32Array(newLen);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < result.length) {
        const nextOffsetBuffer = Math.floor((offsetResult + 1) * ratio);
        let acc = 0;
        let count = 0;
        for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
            acc += buffer[i];
            count++;
        }
        result[offsetResult] = acc / (count || 1);
        offsetResult++;
        offsetBuffer = nextOffsetBuffer;
    }
    return result;
};
const floatTo16BitPCM = (float32Array) => {
    const len = float32Array.length;
    const result = new Int16Array(len);
    for (let i = 0; i < len; i++) {
        let s = Math.max(-1, Math.min(1, float32Array[i]));
        result[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return result;
};
const useSttStream = ({ onFinal }) => {
    const [isRecognizing, setIsRecognizing] = (0, react_1.useState)(false);
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const wsRef = (0, react_1.useRef)(null);
    const streamRef = (0, react_1.useRef)(null);
    const audioCtxRef = (0, react_1.useRef)(null);
    const sourceRef = (0, react_1.useRef)(null);
    const processorRef = (0, react_1.useRef)(null);
    const cleanup = (0, react_1.useCallback)(() => {
        var _a, _b, _c;
        try {
            (_a = processorRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            if (processorRef.current)
                processorRef.current.onaudioprocess = null;
            (_b = sourceRef.current) === null || _b === void 0 ? void 0 : _b.disconnect();
            (_c = audioCtxRef.current) === null || _c === void 0 ? void 0 : _c.close();
        }
        catch (_d) {
            /* ignore */
        }
        processorRef.current = null;
        sourceRef.current = null;
        audioCtxRef.current = null;
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (wsRef.current) {
            try {
                wsRef.current.close();
            }
            catch (_e) {
                /* ignore */
            }
            wsRef.current = null;
        }
        setIsRecognizing(false);
    }, []);
    const stopRecognition = (0, react_1.useCallback)(() => {
        cleanup();
    }, [cleanup]);
    const startRecognition = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        if (isRecognizing || wsRef.current)
            return;
        setIsLoading(true);
        try {
            const stream = yield navigator.mediaDevices.getUserMedia({ audio: true });
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const source = audioCtx.createMediaStreamSource(stream);
            const processor = audioCtx.createScriptProcessor(4096, 1, 1);
            const ws = new WebSocket(STT_WS_URL);
            ws.binaryType = "arraybuffer";
            ws.onmessage = (e) => {
                const text = String(e.data || "");
                if (text.startsWith("final:")) {
                    const phrase = text.slice("final:".length).trim();
                    if (phrase)
                        onFinal(phrase);
                }
            };
            ws.onerror = () => stop();
            ws.onclose = () => stop();
            processor.onaudioprocess = (e) => {
                if (!ws || ws.readyState !== WebSocket.OPEN)
                    return;
                const input = e.inputBuffer.getChannelData(0);
                const downsampled = downsampleBuffer(input, audioCtx.sampleRate, SAMPLE_RATE);
                const pcm16 = floatTo16BitPCM(downsampled);
                ws.send(pcm16.buffer);
            };
            source.connect(processor);
            processor.connect(audioCtx.destination);
            wsRef.current = ws;
            streamRef.current = stream;
            audioCtxRef.current = audioCtx;
            sourceRef.current = source;
            processorRef.current = processor;
            setIsRecognizing(true);
        }
        catch (e) {
            console.error("STT start error", e);
            cleanup();
        }
        finally {
            setIsLoading(false);
        }
    }), [cleanup, isRecognizing, onFinal, stopRecognition]);
    (0, react_1.useEffect)(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);
    return {
        startRecognition,
        stopRecognition,
        isRecognizing,
        isLoading,
    };
};
exports.useSttStream = useSttStream;
